<script lang="ts">
import type { VariantProps } from 'class-variance-authority'
import type { HTMLAttributes, InjectionKey, Ref } from 'vue'
import { cva } from 'class-variance-authority'

export const cardVariants = cva('group/card rounded-lg border border-default text-primal', {
  variants: {
    variant: {
      default: 'bg-white',
      gray: 'bg-neutral-400',
      neutral: 'bg-neutral-300',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

export type CardVariants = VariantProps<typeof cardVariants>

export interface CardProps {
  class?: HTMLAttributes['class']
  variant?: CardVariants['variant']
  collapsible?: boolean
  open?: boolean
  defaultOpen?: boolean
  storageKey?: string
}

export interface CardCollapsibleContext {
  collapsible: Readonly<Ref<boolean>>
  open: Readonly<Ref<boolean>>
  toggle: () => void
}

export const CARD_COLLAPSIBLE_KEY: InjectionKey<CardCollapsibleContext> = Symbol('card-collapsible')
</script>

<script setup lang="ts">
import { useStorage } from '@vueuse/core'
import { computed, provide, ref, toRef } from 'vue'
import { cn } from '#ui-kit/lib/utils'
import { Collapsible } from '#ui-kit/ui/collapsible'

const props = withDefaults(defineProps<CardProps>(), {
  variant: 'default',
  open: undefined,
  defaultOpen: true,
})

const emit = defineEmits<{
  'update:open': [open: boolean]
}>()

const stored = props.storageKey
  ? useStorage<boolean>(`forgd:card-open-state:${props.storageKey}`, props.defaultOpen, undefined, {
      initOnMounted: true,
    })
  : ref(props.defaultOpen)

const isOpen = computed({
  get: () => props.open ?? stored.value,
  set: (value) => {
    stored.value = value
    emit('update:open', value)
  },
})

provide(CARD_COLLAPSIBLE_KEY, {
  collapsible: toRef(props, 'collapsible'),
  open: isOpen,
  toggle: () => {
    isOpen.value = !isOpen.value
  },
})
</script>

<template>
  <Collapsible
    v-if="collapsible"
    :open="isOpen"
    :unmount-on-hide="false"
    data-slot="card"
    :data-variant="variant"
    :class="cn(cardVariants({ variant }), props.class)"
    @update:open="isOpen = $event"
  >
    <slot />
  </Collapsible>
  <div v-else data-slot="card" :data-variant="variant" :class="cn(cardVariants({ variant }), props.class)">
    <slot />
  </div>
</template>
